import { TargetingListList } from './targeting-list-list.model';
import { DistributionRecipientApiModel, DistributionApiModel, AdhocRecipientApiModel } from '../../../api/distributions/model/distribution-api.model';
import { DistributionRecipientModel } from './distribution-recipient-model';
import { RecipientGroup } from './recipient-group.model';
import { TargetingSearchList } from './targeting-search-list.model';
import { RecipientRow } from '../components/Distribution/RecipientEditor/recipient-editor.service';
import { MailingListModel } from '../../../Common/MailingLists/models/mailingList.model';
import { PortfolioModel } from '../components/Portfolios/portfolio.model';
import { RecipientType } from '../enums/recipient-type.enum';
import { DistributionSourceType } from '../enums/distribution-source-type.enum';
import { ToggleAction } from '../enums/toggle-action.enum';

export class RecipientInformationModel {
    total: number = 0;

    recipientGroups: RecipientGroup[] = [];
    individualRecipientsListId: number = null;
    recommendedRecipientsListId: number = null;

    constructor(apiModel?: DistributionApiModel) {
        if (apiModel) {
            this.individualRecipientsListId = apiModel.individualRecipientsListId;
            this.recommendedRecipientsListId = apiModel.recommendedRecipientsListId;

            if (apiModel.adhocRecipients) {
                for (const emailRecipient of apiModel.adhocRecipients) {
                    this.addEmail(emailRecipient.email);
                }
            }

            if (apiModel.recipients) {
                for (const apiRecipient of apiModel.recipients) {
                    // locked searches will have both a targetingListId and targetingSearchId
                    if (apiRecipient.targetingListId && !apiRecipient.targetingSearchId) {
                        if (apiRecipient.targetingListId === this.individualRecipientsListId) {
                            this.getOrCreateGroup(RecipientType.individual).recipients.push(new DistributionRecipientModel(apiRecipient));
                        } else if (apiRecipient.targetingListId === this.recommendedRecipientsListId) {
                            this.getOrCreateGroup(RecipientType.recommended).recipients.push(new DistributionRecipientModel(apiRecipient));
                        } else {
                            this.getOrCreateGroup(RecipientType.list).recipients.push(new DistributionRecipientModel(apiRecipient));
                        }
                    } else if (apiRecipient.targetingSearchId) {
                        this.getOrCreateGroup(RecipientType.search).recipients.push(new DistributionRecipientModel(apiRecipient));
                    } else if (apiRecipient.portfolioId) {
                        this.getOrCreateGroup(RecipientType.portfolio).recipients.push(new DistributionRecipientModel(apiRecipient));
                    } else {
                        this.getOrCreateGroup(RecipientType.mailingList).recipients.push(new DistributionRecipientModel(apiRecipient));
                    }
                }
            }
        }
    }

    calculateTotals() {
        this.total = 0;
        for (const group of this.recipientGroups) {
            group.total = 0;
            for (const recipient of group.recipients) {
                if (recipient.count) {
                    group.total += recipient.count;
                }
            }
            for (const emailRecipient of group.emailRecipients) {
                group.total++;
            }
            this.total += group.total;
        }
    }

    public addPortfolio(portfolio: PortfolioModel) {
        const portfolioRecipients = this.getOrCreateGroup(RecipientType.portfolio);
        let portfolioRecipient = portfolioRecipients.recipients.find(x => x.portfolioId === portfolio.targetingListId);

        if (!portfolioRecipient) {
            portfolioRecipient = new DistributionRecipientModel(null);
            portfolioRecipient.portfolioId = portfolio.targetingListId;
            portfolioRecipients.recipients.push(portfolioRecipient);
        }

        portfolioRecipient.count = portfolio.count;
        portfolioRecipient.label = portfolio.metadata.displayName;

        this.calculateTotals();
    }

    public addPortfolioRow(portfolio: RecipientRow) {
        const portfolioRecipients = this.getOrCreateGroup(RecipientType.portfolio);
        let portfolioRecipient = portfolioRecipients.recipients.find(x => x.portfolioId === portfolio.id);

        if (!portfolioRecipient) {
            portfolioRecipient = new DistributionRecipientModel(null);
            portfolioRecipient.portfolioId = portfolio.id;
            portfolioRecipients.recipients.push(portfolioRecipient);
        }

        portfolioRecipient.count = portfolio.count;
        portfolioRecipient.label = portfolio.name;

        this.calculateTotals();
    }

    public removePortfolio(list: RecipientRow | DistributionRecipientModel) {
        let id;
        if (list instanceof DistributionRecipientModel) {
            id = list.portfolioId;
        } else {
            id = list.id;
        }
        const recipients = this.getGroupRecipientsByRecipientType(RecipientType.portfolio);
        recipients.splice(recipients.findIndex(x => x.portfolioId === id), 1);
        if (recipients.length === 0) {
            this.recipientGroups.splice(this.recipientGroups.findIndex(x => x.type === RecipientType.portfolio), 1);
        }
        this.calculateTotals();
    }


    public addList(list: TargetingListList | RecipientRow) {
        const listType = list.id === this.recommendedRecipientsListId ? RecipientType.recommended :
            (list.id === this.individualRecipientsListId ? RecipientType.individual : RecipientType.list);
        const listRecipients = this.getOrCreateGroup(listType);
        let listRecipient = listRecipients.recipients.find(x => x.targetingListId === list.id);
        if (!listRecipient) {
            listRecipient = new DistributionRecipientModel(null);
            listRecipient.targetingListId = list.id;
            listRecipients.recipients.push(listRecipient);
        }

        listRecipient.count = list.count;
        listRecipient.contactCount = list.contactCount;
        listRecipient.outletCount = list.outletCount;
        listRecipient.label = list.name;
        this.calculateTotals();
    }

    public removeList(list: RecipientRow | DistributionRecipientModel) {
        let id;
        if (list instanceof DistributionRecipientModel) {
            id = list.targetingListId;
        } else {
            id = list.id;
        }

        const removeIndividualRecipientsList = id === this.individualRecipientsListId;
        const removeRecommendedRecipientsList = id === this.recommendedRecipientsListId;

        const listType = id === this.recommendedRecipientsListId ? RecipientType.recommended :
            (id === this.individualRecipientsListId ? RecipientType.individual : RecipientType.list);
        const recipients = this.getGroupRecipientsByRecipientType(listType);
        recipients.splice(recipients.findIndex(x => x.targetingListId === id), 1);
        if (recipients.length === 0) {
            this.recipientGroups.splice(this.recipientGroups.findIndex(x =>
                x.type === listType), 1);
        }

        // setting individualRecipientsListId to null to clear the list
        // otherwise, next time when adding contacts/outlets to distribution, old contacts/outlets are still there
        if (removeIndividualRecipientsList) {
            this.individualRecipientsListId = null;
        }
        if (removeRecommendedRecipientsList) {
            this.recommendedRecipientsListId = null;
        }

        this.calculateTotals();
    }

    public addSearch(search: TargetingSearchList | RecipientRow) {
        const searchRecipients = this.getOrCreateGroup(RecipientType.search);
        let searchRecipient = searchRecipients.recipients.find(x => x.targetingSearchId === search.id);
        if (!searchRecipient) {
            searchRecipient = new DistributionRecipientModel(null);
            searchRecipient.targetingSearchId = search.id;
            searchRecipients.recipients.push(searchRecipient);
        }
        if (search.name) {
            searchRecipient.label = search.name;
        }

        if (search.count) {
            searchRecipient.count = search.count;
            this.calculateTotals();
        }
    }

    public removeSearch(search: RecipientRow | DistributionRecipientModel) {
        let id;
        if (search instanceof DistributionRecipientModel) {
            id = search.targetingSearchId;
        } else {
            id = search.id;
        }
        const recipients = this.getGroupRecipientsByRecipientType(RecipientType.search);
        recipients.splice(recipients.findIndex(x => x.targetingSearchId === id), 1);
        if (recipients.length === 0) {
            this.recipientGroups.splice(this.recipientGroups.findIndex(x => x.type === RecipientType.search), 1);
        }
        this.calculateTotals();
    }

    public addMailingList(mailingList: MailingListModel | RecipientRow) {
        const mailingListRecipients = this.getOrCreateGroup(RecipientType.mailingList);
        let mailingListRecipient = mailingListRecipients.recipients.find(x => x.mailingListId === mailingList.id);
        if (!mailingListRecipient) {
            mailingListRecipient = new DistributionRecipientModel(null);
            mailingListRecipient.mailingListId = mailingList.id;
            mailingListRecipients.recipients.push(mailingListRecipient);
        }

        mailingListRecipient.count = mailingList.count;
        mailingListRecipient.label = mailingList.name;
        this.calculateTotals();
    }

    public removeMailingList(mailingList: RecipientRow | DistributionRecipientModel) {
        let id;
        if (mailingList instanceof DistributionRecipientModel) {
            id = mailingList.mailingListId;
        } else {
            id = mailingList.id;
        }
        const recipients = this.getGroupRecipientsByRecipientType(RecipientType.mailingList);
        recipients.splice(recipients.findIndex(x => x.mailingListId === id), 1);
        if (recipients.length === 0) {
            this.recipientGroups.splice(this.recipientGroups.findIndex(x => x.type === RecipientType.mailingList), 1);
        }
        this.calculateTotals();
    }

    public addEmail(email: string) {
        const emailRecipients = this.getOrCreateGroup(RecipientType.email);
        const emailAlreadyExists = emailRecipients.emailRecipients.some(itm => itm.trim().toLowerCase() === email.trim().toLowerCase());
        if(!emailAlreadyExists) {
            emailRecipients.emailRecipients.push(email);
            emailRecipients.emailRecipients.sort((x1, x2) => x1.localeCompare(x2));
            emailRecipients.total++;
            this.total++;
        }
    }

    public addEmails(emails: string[]) {
        emails.forEach(email => this.addEmail(email));
    }

    public removeEmail(email: string) {
        const emailRecipients = this.getOrCreateGroup(RecipientType.email);
        emailRecipients.emailRecipients.splice(emailRecipients.emailRecipients.indexOf(email), 1);
        emailRecipients.total--;
        this.total--;
        if (emailRecipients.emailRecipients.length === 0) {
            this.recipientGroups.splice(this.recipientGroups.findIndex(x => x.type === RecipientType.email), 1);
        }
    }

    public getOrCreateGroup(type: RecipientType): RecipientGroup {
        let recipientGroup = this.recipientGroups.find(x => x.type === type);
        if (!recipientGroup) {
            switch (type) {
                case RecipientType.list:
                    recipientGroup = new RecipientGroup(RecipientType.list, 'Discover.recipientEditor.labels.lists');
                    break;
                case RecipientType.search:
                    recipientGroup = new RecipientGroup(RecipientType.search, 'Discover.leftSidebar.searches');
                    break;
                case RecipientType.portfolio:
                    recipientGroup = new RecipientGroup(RecipientType.portfolio, 'Core.navigation.advDiscover.portfolios');
                    break;
                case RecipientType.mailingList:
                    recipientGroup = new RecipientGroup(RecipientType.mailingList, 'Discover.recipientEditor.labels.mailingLists');
                    break;
                case RecipientType.email:
                    recipientGroup = new RecipientGroup(RecipientType.email, 'Discover.recipientEditor.labels.additionalRecipients');
                    break;
                case RecipientType.individual:
                    recipientGroup = new RecipientGroup(RecipientType.individual, 'Discover.recipientEditor.labels.individualRecipients');
                    break;
                case RecipientType.recommended:
                    recipientGroup = new RecipientGroup(RecipientType.recommended, 'Discover.recipientEditor.labels.prCopilot');
                    break;
            }
            this.recipientGroups.push(recipientGroup);
        }
        this.recipientGroups.sort((x1, x2) => x1.type - x2.type);
        return recipientGroup;
    }

    public getRecipientGroup(type: RecipientType): RecipientGroup {
        return this.recipientGroups.find(x => x.type === type);
    }

    toggleLockState(searchIds: number[], action: ToggleAction) {
        const searchGroup = this.recipientGroups.find(x => x.type === RecipientType.search);

        if (searchGroup) {
            searchGroup.recipients.forEach(recipient => {
                if (searchIds.includes(recipient.targetingSearchId)) {
                    if (action === ToggleAction.LOCK) {
                        recipient.isLocked = true;
                    } else {
                        recipient.isLocked = false;
                        recipient.targetingListId = null;
                    }
                }
            });
        }
    }

    equals(other: RecipientInformationModel) {
        if (this.recipientGroups.length !== other.recipientGroups.length) {
            return false;
        }

        for (const recipientGroup of this.recipientGroups) {
            if (!other.recipientGroups.find(x => x.equals(recipientGroup))) {
                return false;
            }
        }
        return true;
    }

    toApiRecipientModel(): DistributionRecipientApiModel[] {
        const recipients = [];

        const listGroup = this.recipientGroups.find(x => x.type === RecipientType.list);
        if (listGroup) {
            for (const listRecipient of listGroup.recipients) {
                recipients.push(listRecipient.toApiModel());
            }
        }

        const portfolioGroup = this.recipientGroups.find(x => x.type === RecipientType.portfolio);
        if (portfolioGroup) {
            for (const portfolioRecipient of portfolioGroup.recipients) {
                recipients.push(portfolioRecipient.toApiModel());
            }
        }

        const searchGroup = this.recipientGroups.find(x => x.type === RecipientType.search);
        if (searchGroup) {
            for (const searchRecipient of searchGroup.recipients) {
                recipients.push(searchRecipient.toApiModel());
            }
        }

        const mailingListGroup = this.recipientGroups.find(x => x.type === RecipientType.mailingList);
        if (mailingListGroup) {
            for (const mailingListRecipient of mailingListGroup.recipients) {
                recipients.push(mailingListRecipient.toApiModel());
            }
        }

        return recipients;
    }

    getEmailRecipients(): string[] {
        const emailGroup = this.recipientGroups.find(x => x.type === RecipientType.email);
        if (emailGroup) {
            return emailGroup.emailRecipients;
        }
        return [];
    }

    toApiAdhocRecipients(): AdhocRecipientApiModel[] {
        const adhocRecipients = this.getEmailRecipients().map(email => {
            return {
                email: email,
                firstName: '',
                lastName: ''
            } as AdhocRecipientApiModel;
        });

        if (adhocRecipients?.length > 0) {
            return adhocRecipients;
        }

        return [];
    }

    getGroupRecipientsByRecipientType(recipientType: RecipientType): DistributionRecipientModel[] {
        const recipientGroup = this.recipientGroups.find(x => x.type === recipientType);
        if (recipientGroup) {
            return recipientGroup.recipients;
        }
        return [];
    }

    getGroupRecipientsBySourceType(distributionSourceType: DistributionSourceType): DistributionRecipientModel[] {
        let recipientType;
        switch (distributionSourceType) {
            case DistributionSourceType.searches:
                recipientType = RecipientType.search;
                break;
            case DistributionSourceType.mailingLists:
                recipientType = RecipientType.mailingList;
                break;
            case DistributionSourceType.portfolios:
                recipientType = RecipientType.portfolio;
                break;
            case DistributionSourceType.lists:
            default:
                recipientType = RecipientType.list;
                break;
        }

        return this.getGroupRecipientsByRecipientType(recipientType);
    }

    clone(): RecipientInformationModel {
        const clone = new RecipientInformationModel();
        clone.total = this.total;
        clone.individualRecipientsListId = this.individualRecipientsListId;
        clone.recommendedRecipientsListId = this.recommendedRecipientsListId;
        clone.recipientGroups = [];
        for (const group of this.recipientGroups) {
            const newGroup = new RecipientGroup(group.type, group.label);
            newGroup.total = group.total;
            newGroup.emailRecipients = [...group.emailRecipients];
            newGroup.recipients = [...group.recipients.map(recipient => recipient.clone())];
            clone.recipientGroups.push(newGroup);
        }
        return clone;
    }
}
